import {
	CategoryScale,
	Chart as ChartJS,
	ChartOptions,
	Filler,
	Legend,
	LineElement,
	LinearScale,
	PointElement,
	ScriptableContext,
	Title,
	Tooltip,
} from 'chart.js';
import _ from 'lodash';
import { Line } from 'react-chartjs-2';

import { tokens } from '../theme/tokens';

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler,
);

const chartAreaBackgroundPlugin = {
	id: 'chartAreaBackground',
	beforeDraw(chart: ChartJS) {
		const { ctx, chartArea } = chart;
		if (!chartArea) return;
		ctx.save();
		ctx.fillStyle = tokens.surface.primary;
		ctx.fillRect(
			chartArea.left,
			chartArea.top,
			chartArea.right - chartArea.left,
			chartArea.bottom - chartArea.top,
		);
		ctx.restore();
	},
};

const hoverVerticalLinePlugin = {
	id: 'hoverVerticalLine',
	beforeDatasetsDraw(chart: ChartJS) {
		const activeElements = chart.tooltip?.getActiveElements();
		if (!activeElements?.length) return;

		const { ctx, chartArea } = chart;
		if (!chartArea) return;

		const x = activeElements[0].element.x;
		ctx.save();
		ctx.beginPath();
		ctx.moveTo(x, chartArea.top);
		ctx.lineTo(x, chartArea.bottom);
		ctx.lineWidth = 1;
		ctx.strokeStyle = tokens.border.default;
		ctx.setLineDash([3, 3]);
		ctx.stroke();
		ctx.restore();
	},
};

export function LineChart(props: {
	labels: string[];
	data: number[] | { x: number; y: number }[];
	height?: number;
	bounds?: [number, number];
	autoBound?: boolean;
	compactLayout?: boolean;
	minimalLayout?: boolean;
	showBackground?: boolean;
	// Draws a filled dot on the final datapoint; all other points stay hidden.
	highlightLastPoint?: boolean;
	// By default the chart bleeds into the surrounding padding (negative margin)
	// so it can touch a card's edges. Set false to keep it inside the padding.
	bleed?: boolean;
	yFormat?: (x: number) => string;
	tintColor?: string;
}) {
	const tint = props.tintColor ?? tokens.brand.icon;
	const bleed = props.bleed ?? true;

	let [min, max] = props.bounds ?? [undefined, undefined];
	if (props.autoBound && props.data.length > 0) {
		const ys =
			typeof props.data[0] === 'number'
				? (props.data as number[])
				: (props.data as { y: number }[]).map((p) => p.y);
		const [absMin, absMax] = [_.min(ys) ?? 0, _.max(ys) ?? 0];
		const minMargin = 1e-3;
		const rangeOrder = Math.round(Math.log10(absMax - absMin + minMargin * 2));
		const maxOffset = Math.pow(10, rangeOrder);

		min = Math.floor((absMin - minMargin) / maxOffset) * maxOffset;
		max = Math.ceil((absMax + minMargin) / maxOffset) * maxOffset;

		if (props.bounds) {
			min = Math.max(min, props.bounds[0]);
			max = Math.min(max, props.bounds[1]);
		}
	}

	const hexColorGradient = (context: CanvasRenderingContext2D, color: string) => {
		const [red, green, blue] = [
			color.substring(1, 3),
			color.substring(3, 5),
			color.substring(5, 7),
		].map((s) => parseInt(s, 16));

		const gradient = context.createLinearGradient(0, 0, 0, props.height ?? 200);
		gradient.addColorStop(0, `rgba(${red}, ${green}, ${blue}, 0.4)`);
		gradient.addColorStop(1, `rgba(${red}, ${green}, ${blue}, 0.0)`);

		return gradient;
	};

	const lastIndex = props.data.length - 1;
	const perPoint = <T,>(visible: T, hidden: T): T[] =>
		props.data.map((_value, index) => (index === lastIndex ? visible : hidden));

	const data = () => {
		return {
			labels: props.labels,
			datasets: [
				{
					data: props.data,
					fill: 'start',
					backgroundColor: (context: ScriptableContext<'line'>) =>
						hexColorGradient(context.chart.ctx, tint),
					borderColor: tint,
					pointBackgroundColor: props.highlightLastPoint
						? perPoint(tint, 'transparent')
						: 'transparent',
					pointBorderColor: props.highlightLastPoint
						? perPoint(tokens.border.white, 'transparent')
						: 'transparent',
					pointBorderWidth: props.highlightLastPoint ? perPoint(2, 0) : 0,
					pointHoverBackgroundColor: tint,
					pointHoverBorderColor: tokens.border.white,
					pointHoverBorderWidth: 2,
					pointRadius: props.highlightLastPoint ? perPoint(6, 0) : 0,
					pointHoverRadius: 8,
					pointHitRadius: 20,
					pointStyle: 'circle',
				},
			],
		};
	};

	const tickFont = {
		weight: 'normal' as const,
		size: 12,
		family: tokens.typography.fontFamily,
	};

	const options: ChartOptions<'line'> = {
		maintainAspectRatio: false,
		responsive: true,
		layout: {
			padding: bleed
				? {
						top: 16,
						bottom: 20,
						left: 20,
						right: 20,
					}
				: {
						top: 10,
						bottom: 10,
						left: props.minimalLayout && props.highlightLastPoint ? 10 : 0,
						right: props.highlightLastPoint ? 10 : 0,
					},
		},
		scales: {
			x: {
				display: !(props.compactLayout || props.minimalLayout),
				grid: {
					display: false,
				},
				ticks: {
					display: !props.compactLayout,
					color: tokens.text.secondary,
					font: tickFont,
				},
			},
			y: {
				display: !props.minimalLayout,
				beginAtZero: true,
				min,
				max,
				grid: {
					display: true,
					color: tokens.border.subtle,
				},
				ticks: {
					precision: 0,
					callback: props.yFormat,
					color: tokens.text.secondary,
					font: tickFont,
				},
			},
		},
		elements: {
			line: {
				tension: 0.35,
			},
		},
		plugins: {
			filler: {
				propagate: false,
			},
			legend: {
				display: false,
			},
			tooltip: {
				enabled: true,
				displayColors: false,
				callbacks: {
					label: function (context: any) {
						const source = context.dataset.data[context.dataIndex];

						if (source?.tooltip) {
							return source?.tooltip;
						}

						return props.yFormat ? props.yFormat(context.parsed.y) : context.parsed.y.toFixed(1);
					},
				},
			},
		},
		interaction: {
			mode: 'index',
			intersect: false,
		},
	};

	const chartPlugins = [
		...(props.showBackground ? [chartAreaBackgroundPlugin] : []),
		hoverVerticalLinePlugin,
	];

	return (
		// isolate: the canvas stacks above its own background, but that z-index
		// must not compete with sticky headers or portalled popovers.
		<div style={{ margin: bleed ? '-10px -20px -20px -20px' : undefined, isolation: 'isolate' }}>
			<div style={{ position: 'relative', zIndex: 2, height: props.height }}>
				<Line
					key={props.showBackground ? 'with-bg' : 'no-bg'}
					data={data() as any}
					options={options}
					plugins={chartPlugins}
					height={props.height}
				/>
			</div>
		</div>
	);
}
